import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Button,
  cardStyle,
  dangerButtonStyle,
  eyebrowStyle,
  ghostButtonStyle,
  ghostLinkStyle,
  inputStyle,
  primaryButtonStyle,
  Surface,
} from '@kilat/ui';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import {
  LOYALTY_CONFIG_DOC_ID,
  loyaltyConfigRepoFor,
  loyaltyTiersRepoFor,
} from '../lib/repositories/loyalty-repo.js';
import { voucherTemplatesRepoFor } from '../lib/repositories/vouchers-repo.js';
import { logger } from '../lib/logger.js';
import { PAGE_FRAME_MAX, gridFullRowStyle, sectionGridStyle } from '../lib/layout.js';
import {
  buildLoyaltySettingsPatch,
  rewardError,
  settingsDraftFromConfig,
  type RewardDraft,
  type SettingsDraft,
} from '../lib/loyalty-config-patch.js';
import type { I18nString, LoyaltyConfig, Tier, TierPerk, VoucherTemplate } from '@kilat/shared';

/**
 * /loyalty/settings — the operator's loyalty-program admin.
 *
 * Two editors over the tenant loyalty program, which was seed-only until now:
 *   1. Earning rules — the single config doc at
 *      `tenants/{tid}/loyaltyProgram/config` (LoyaltyConfigSchema): the
 *      earn-enabled toggle, points-per-RM, points expiry, and the per-mechanic
 *      feature toggles. This is exactly what `onTicketCollected` → `computePoints`
 *      reads at award time, so editing it here changes real earning.
 *   2. Tier ladder — the subcollection at
 *      `tenants/{tid}/loyaltyProgram/config/tiers/{tierId}` (TierSchema):
 *      list / create / edit / delete, ordered by rank. `evalTier` reads these
 *      on every award to place the customer.
 *
 * Both are rules-guarded client writes (owner/manager) — see loyalty-repo.ts.
 * Per [[feedback-no-dead-ends]]: every surface has explicit
 * loading / empty / error / saving / saved states with a next action.
 *
 * Deferred to a next slice (noted in docs/features/loyalty-engine.feature.md):
 * the `vehicleMultipliers` matrix and `timeBonuses` window editor (both need
 * extra catalog wiring), and a per-locale authoring UI for tier names + perk
 * descriptions — this slice authors English and PRESERVES any existing
 * ms/zh/ta translations on edit.
 */

// ── perk kinds that carry an editable numeric value ──────────────────────
const PERK_KINDS: readonly TierPerk['kind'][] = [
  'discount_pct',
  'bonus_points_multiplier',
  'free_upgrade_per_period',
  'queue_priority',
  'custom',
];

export function LoyaltySettingsPage() {
  const { t } = useI18n();
  const { user, claims, loading: authLoading } = useAuth();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  if (authLoading) return <Splash label={t('dashboard.loyaltyAdmin.loading')} />;
  if (!user) return <Splash label={t('dashboard.loyaltyAdmin.signIn')} />;
  if (!tenantId) return <Splash label={t('dashboard.loyaltyAdmin.loadingAccount')} />;

  return (
    <main style={pageStyle}>
      <Header />
      <section style={bodyStyle}>
        <SettingsEditor tenantId={tenantId} />
        <TierLadderEditor tenantId={tenantId} />
      </section>
    </main>
  );
}

function Header() {
  const { t } = useI18n();
  return (
    <header style={headerStyle}>
      <div style={headerInnerStyle}>
        <div>
          <p style={eyebrowStyle}>{t('dashboard.loyaltyAdmin.eyebrow')}</p>
          <h1 style={h1Style}>{t('dashboard.loyaltyAdmin.title')}</h1>
        </div>
        <Link to="/queue" style={ghostLinkStyle}>
          {t('dashboard.loyaltyAdmin.backToQueue')}
        </Link>
      </div>
    </header>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// 1. Earning-rules editor
// ══════════════════════════════════════════════════════════════════════════

const FEATURE_KEYS: readonly (keyof LoyaltyConfig['enabledFeatures'])[] = [
  'tiers',
  'punchCard',
  'achievements',
  'challenges',
  'spinWheel',
  'referrals',
  'birthdayBonus',
  'streaks',
];

// The punch-card, achievements, streaks, challenges, referrals, spin-wheel +
// birthday-bonus toggles are LIVE controls (the engine reads them) — they must
// not sit in the inert "coming soon" group with the not-yet-wired mechanics.
// Each has its own dedicated section below (the spin wheel's enable toggle lives
// on its own /loyalty/spin-wheel authoring page, gated on having >= 2 prize
// segments).
const COMING_SOON_FEATURE_KEYS: readonly (keyof LoyaltyConfig['enabledFeatures'])[] =
  FEATURE_KEYS.filter(
    (k) =>
      k !== 'punchCard' &&
      k !== 'achievements' &&
      k !== 'streaks' &&
      k !== 'challenges' &&
      k !== 'referrals' &&
      k !== 'spinWheel' &&
      k !== 'birthdayBonus',
  );

type SaveStage =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'failed'; message: string };

function SettingsEditor({ tenantId }: { readonly tenantId: string }) {
  const { t } = useI18n();
  const repo = useMemo(() => loyaltyConfigRepoFor(tenantId), [tenantId]);
  const subscribe = useMemo(
    () => (cb: (v: LoyaltyConfig | null) => void) => repo.snapshot(LOYALTY_CONFIG_DOC_ID, cb),
    [repo],
  );
  const { data: config, loading } = useTrackedSnapshot<LoyaltyConfig | null>(
    ['loyalty-config', tenantId],
    subscribe,
  );

  // Voucher templates power the punch-card reward picker. Bounded live listener
  // with a paired unsubscribe (useTrackedSnapshot); errors degrade to an empty
  // list so the picker shows its "create a voucher first" empty state rather
  // than a dead-end spinner.
  const templatesRepo = useMemo(() => voucherTemplatesRepoFor(tenantId), [tenantId]);
  const templatesSub = useMemo(
    () => (cb: (v: VoucherTemplate[]) => void) => templatesRepo.listSnapshot(cb, () => cb([])),
    [templatesRepo],
  );
  const { data: templates, loading: templatesLoading } = useTrackedSnapshot<VoucherTemplate[]>(
    ['loyalty-voucher-templates', tenantId],
    templatesSub,
  );

  const [draft, setDraft] = useState<SettingsDraft | null>(null);
  const [baseline, setBaseline] = useState<SettingsDraft | null>(null);
  const [stage, setStage] = useState<SaveStage>({ kind: 'idle' });

  // Initialise the form once, when the config first arrives. Later live
  // snapshots don't clobber operator edits (single-operator config surface).
  useEffect(() => {
    if (config && !draft) {
      const d = settingsDraftFromConfig(config);
      setDraft(d);
      setBaseline(d);
    }
  }, [config, draft]);

  const isDirty = useMemo(
    () => draft !== null && baseline !== null && JSON.stringify(draft) !== JSON.stringify(baseline),
    [draft, baseline],
  );

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!draft || !config) return;
    const rate = Number(draft.pointsPerRM);
    if (!Number.isFinite(rate) || rate < 0) {
      setStage({ kind: 'failed', message: t('dashboard.loyaltyAdmin.settings.invalidRate') });
      return;
    }
    const expiry = Number.parseInt(draft.expiryDays, 10);
    if (!Number.isFinite(expiry) || expiry < 0) {
      setStage({ kind: 'failed', message: t('dashboard.loyaltyAdmin.settings.invalidExpiry') });
      return;
    }
    const washes = Number.parseInt(draft.punchWashesRequired, 10);
    if (draft.features.punchCard && (!Number.isFinite(washes) || washes < 2 || washes > 50)) {
      setStage({ kind: 'failed', message: t('dashboard.loyaltyAdmin.punchCard.invalidWashes') });
      return;
    }
    // Referral rewards only need to be valid when the feature is switched ON —
    // a 'none' side is always fine; a points side needs a positive amount; a
    // voucher side needs a template chosen (no dead-end config).
    if (draft.features.referrals) {
      const refErr = rewardError(draft.referrerReward) ?? rewardError(draft.refereeReward);
      if (refErr) {
        setStage({ kind: 'failed', message: t(refErr) });
        return;
      }
    }
    // Birthday reward only needs to be valid when the feature is switched ON —
    // 'none' is always fine (the flag is on but nothing is granted); a points
    // side needs a positive amount; a voucher side needs a template chosen.
    if (draft.features.birthdayBonus) {
      const bdErr = rewardError(draft.birthdayReward);
      if (bdErr) {
        setStage({ kind: 'failed', message: t(bdErr) });
        return;
      }
    }
    setStage({ kind: 'saving' });
    try {
      // G4: a FIELD PATCH, never a full-document write. This doc is co-owned —
      // /loyalty/promotions writes `promotionsPaused` (the kill switch read on
      // the money path) and /loyalty/spin-wheel writes the prize table. A
      // full-doc `set()` from this page's snapshot could carry a stale pause
      // flag and silently resume every discount, and with offline persistence
      // that write can flush hours later. See lib/loyalty-config-patch.ts for
      // the field-ownership map; infra/firestore.rules enforces it server side.
      await repo.update(
        LOYALTY_CONFIG_DOC_ID,
        buildLoyaltySettingsPatch(draft, {
          pointsPerRM: rate,
          expiryDays: expiry,
          washesRequired: washes,
        }),
      );
      logger.info('loyalty config saved', { tenantId, pointsPerRM: rate, enabled: draft.enabled });
      setBaseline(draft);
      setStage({ kind: 'saved' });
    } catch (err) {
      setStage({
        kind: 'failed',
        message: t('dashboard.loyaltyAdmin.settings.saveFailed', {
          error: (err as Error).message ?? '',
        }),
      });
    }
  }

  return (
    <Surface as="article">
      <SectionHead
        title={t('dashboard.loyaltyAdmin.settings.heading')}
        sub={t('dashboard.loyaltyAdmin.settings.sub')}
      />
      {draft ? (
        // Two columns of sections on a laptop-width screen, one on a phone. The master
        // switch, the coming-soon group and the save bar span the full row.
        <form onSubmit={save} style={{ ...sectionGridStyle, marginTop: 'var(--space-4)' }}>
          <div style={gridFullRowStyle}>
            <ToggleRow
              label={t('dashboard.loyaltyAdmin.settings.enabledLabel')}
              description={
                draft.enabled
                  ? t('dashboard.loyaltyAdmin.settings.enabledOn')
                  : t('dashboard.loyaltyAdmin.settings.enabledOff')
              }
              checked={draft.enabled}
              onChange={(v) => {
                setDraft({ ...draft, enabled: v });
                setStage({ kind: 'idle' });
              }}
            />
          </div>

          <NumberField
            label={t('dashboard.loyaltyAdmin.settings.pointsPerRM')}
            hint={t('dashboard.loyaltyAdmin.settings.pointsPerRMHint')}
            value={draft.pointsPerRM}
            onChange={(v) => {
              setDraft({ ...draft, pointsPerRM: v });
              setStage({ kind: 'idle' });
            }}
          />

          {/* Points expiry moved OUT of the disabled group on 2026-08-26,
              because its engine shipped. `expirePointsDaily`
              (apps/functions/src/loyalty/expirePoints.ts, exported from
              index.ts so it is deployed) reads `LoyaltyConfig.expiryDays`
              nightly and retires lapsed points through `decidePointsExpiry`.

              The comment that used to sit here said no runtime code read this
              field, which was true when it was written and stopped being true
              when that function landed — leaving the control greyed out with a
              "coming soon" note. So the two lies swapped places: the field went
              from a control that did nothing, to a working control an operator
              physically could not reach. `pointerEvents: 'none'` meant they
              could not even click it to find out. The engine's own header
              records why the first version of this mattered: "a control that
              lies is worse than one that is absent, because the operator plans
              their programme around it." Unreachable is the same failure with
              the sign flipped.

              0 still means never, and it is still the default, so no tenant is
              touched until an operator deliberately picks a number. */}
          <NumberField
            label={t('dashboard.loyaltyAdmin.settings.expiryDays')}
            hint={t('dashboard.loyaltyAdmin.settings.expiryHint')}
            value={draft.expiryDays}
            onChange={(v) => {
              setDraft({ ...draft, expiryDays: v });
              setStage({ kind: 'idle' });
            }}
          />

          {/* What genuinely remains inert: `enabledFeatures.tiers`. The
              dashboard writes it (loyalty-config-patch.ts) and NOTHING on the
              server reads it — `evalTier` runs unconditionally in
              `onTicketCollected`. Wiring it is deliberately NOT done here
              because a tier carries a DISCOUNT (`price-with-discounts.ts`), so
              honouring the toggle would change what a customer pays. That is
              the owner's call, not a cleanup. */}
          <fieldset
            disabled
            style={{ ...fieldsetStyle, ...gridFullRowStyle, opacity: 0.62, pointerEvents: 'none' }}
            aria-label={t('dashboard.loyaltyAdmin.settings.comingSoonHeading')}
          >
            <legend style={legendStyle}>
              {t('dashboard.loyaltyAdmin.settings.comingSoonHeading')}
            </legend>
            <p style={metaStyle}>{t('dashboard.loyaltyAdmin.settings.comingSoonNote')}</p>
            <div style={featureGridStyle}>
              {COMING_SOON_FEATURE_KEYS.map((f) => (
                <ToggleRow
                  key={f}
                  compact
                  label={t(`dashboard.loyaltyAdmin.settings.feature.${f}`)}
                  checked={draft.features[f]}
                  onChange={(v) => {
                    setDraft({ ...draft, features: { ...draft.features, [f]: v } });
                    setStage({ kind: 'idle' });
                  }}
                />
              ))}
            </div>
          </fieldset>

          <PunchCardSection
            draft={draft}
            templates={templates ?? []}
            templatesLoading={templatesLoading}
            onChange={(patch) => {
              setDraft({ ...draft, ...patch });
              setStage({ kind: 'idle' });
            }}
          />

          <AchievementsSection
            draft={draft}
            onChange={(patch) => {
              setDraft({ ...draft, ...patch });
              setStage({ kind: 'idle' });
            }}
          />

          <StreaksSection
            draft={draft}
            onChange={(patch) => {
              setDraft({ ...draft, ...patch });
              setStage({ kind: 'idle' });
            }}
          />

          <ChallengesSection
            draft={draft}
            onChange={(patch) => {
              setDraft({ ...draft, ...patch });
              setStage({ kind: 'idle' });
            }}
          />

          <SpinWheelSection config={config} />

          <ReferralsSection
            draft={draft}
            templates={templates ?? []}
            templatesLoading={templatesLoading}
            onChange={(patch) => {
              setDraft({ ...draft, ...patch });
              setStage({ kind: 'idle' });
            }}
          />

          <BirthdayBonusSection
            draft={draft}
            templates={templates ?? []}
            templatesLoading={templatesLoading}
            onChange={(patch) => {
              setDraft({ ...draft, ...patch });
              setStage({ kind: 'idle' });
            }}
          />

          <div style={{ ...saveBarStyle, ...gridFullRowStyle }}>
            <button
              type="submit"
              disabled={!isDirty || stage.kind === 'saving'}
              style={{
                ...primaryButtonStyle,
                opacity: !isDirty || stage.kind === 'saving' ? 0.55 : 1,
              }}
            >
              {stage.kind === 'saving'
                ? t('dashboard.loyaltyAdmin.settings.saving')
                : t('dashboard.loyaltyAdmin.settings.save')}
            </button>
            {isDirty ? (
              <button
                type="button"
                onClick={() => {
                  if (baseline) setDraft(baseline);
                  setStage({ kind: 'idle' });
                }}
                style={ghostButtonStyle}
              >
                {t('dashboard.loyaltyAdmin.settings.discard')}
              </button>
            ) : null}
            <StatusText stage={stage} isDirty={isDirty} />
          </div>
        </form>
      ) : loading ? (
        <InlineNote>{t('dashboard.loyaltyAdmin.settings.loading')}</InlineNote>
      ) : (
        <FeedbackCard
          title={t('dashboard.loyaltyAdmin.settings.errorTitle')}
          body={t('dashboard.loyaltyAdmin.settings.errorBody')}
          actionLabel={t('dashboard.loyaltyAdmin.settings.reload')}
          onAction={() => window.location.reload()}
        />
      )}
    </Surface>
  );
}

// ── punch-card editor (nested in the earning-rules form) ──────────────────

/** Effects that grant a whole free wash — surfaced first in the reward picker. */
function isFreeWashEffect(t: VoucherTemplate): boolean {
  return t.effect.type === 'free_service' || t.effect.type === 'free_package';
}

function PunchCardSection({
  draft,
  templates,
  templatesLoading,
  onChange,
}: {
  readonly draft: SettingsDraft;
  readonly templates: readonly VoucherTemplate[];
  readonly templatesLoading: boolean;
  readonly onChange: (patch: Partial<SettingsDraft>) => void;
}) {
  const { t, locale } = useI18n();
  const on = draft.features.punchCard;

  // Free-wash templates first (they're the natural reward), then the rest.
  const ordered = useMemo(() => {
    const freeWash = templates.filter(isFreeWashEffect);
    const others = templates.filter((tpl) => !isFreeWashEffect(tpl));
    return [...freeWash, ...others];
  }, [templates]);

  const noTemplates = !templatesLoading && templates.length === 0;
  const missingReward = on && draft.punchRewardTemplateId.trim().length === 0;

  return (
    <fieldset style={fieldsetStyle}>
      <legend style={legendStyle}>{t('dashboard.loyaltyAdmin.punchCard.heading')}</legend>
      <p style={metaStyle}>{t('dashboard.loyaltyAdmin.punchCard.sub')}</p>

      <div style={{ marginTop: 'var(--space-3)' }}>
        <ToggleRow
          label={t('dashboard.loyaltyAdmin.punchCard.enableLabel')}
          description={
            on
              ? t('dashboard.loyaltyAdmin.punchCard.enableOn')
              : t('dashboard.loyaltyAdmin.punchCard.enableOff')
          }
          checked={on}
          onChange={(v) => onChange({ features: { ...draft.features, punchCard: v } })}
        />
      </div>

      {on ? (
        <div style={{ display: 'grid', gap: 'var(--space-4)', marginTop: 'var(--space-4)' }}>
          <NumberField
            label={t('dashboard.loyaltyAdmin.punchCard.washesLabel')}
            hint={t('dashboard.loyaltyAdmin.punchCard.washesHint')}
            value={draft.punchWashesRequired}
            onChange={(v) => onChange({ punchWashesRequired: v })}
          />

          {noTemplates ? (
            <div style={emptyStyle}>
              <p style={{ ...metaStyle, margin: 0 }}>
                {t('dashboard.loyaltyAdmin.punchCard.noTemplates')}
              </p>
              <Link
                to="/loyalty/vouchers"
                style={{
                  ...primaryButtonStyle,
                  marginTop: 'var(--space-3)',
                  textDecoration: 'none',
                  display: 'inline-block',
                }}
              >
                {t('dashboard.loyaltyAdmin.punchCard.createVoucher')}
              </Link>
            </div>
          ) : (
            <label style={fieldStyle}>
              <span style={fieldLabelStyle}>
                {t('dashboard.loyaltyAdmin.punchCard.rewardLabel')}
              </span>
              <select
                value={draft.punchRewardTemplateId}
                onChange={(e) => onChange({ punchRewardTemplateId: e.target.value })}
                style={inputStyle}
              >
                <option value="">{t('dashboard.loyaltyAdmin.punchCard.rewardNone')}</option>
                {ordered.map((tpl) => (
                  <option key={tpl.id} value={tpl.id}>
                    {(tpl.name[locale] ?? tpl.name.en ?? tpl.id) +
                      (isFreeWashEffect(tpl)
                        ? ` — ${t('dashboard.loyaltyAdmin.punchCard.freeWashTag')}`
                        : '')}
                  </option>
                ))}
              </select>
              <span style={hintStyle}>{t('dashboard.loyaltyAdmin.punchCard.rewardHint')}</span>
            </label>
          )}

          {missingReward && !noTemplates ? (
            <p role="alert" style={{ ...statusStyle, color: 'var(--color-accent)' }}>
              {t('dashboard.loyaltyAdmin.punchCard.noRewardWarning')}
            </p>
          ) : null}
        </div>
      ) : null}
    </fieldset>
  );
}

// ── achievements section (live toggle + link to the authoring page) ──────
//
// Milestone BADGES are authored on the dedicated /loyalty/achievements CRUD
// page (mirrors how challenges/vouchers live on their own routes). This section
// is just the master opt-in toggle the engine reads at award time plus a link
// into the authoring page — no per-tenant badge config lives here.
function AchievementsSection({
  draft,
  onChange,
}: {
  readonly draft: SettingsDraft;
  readonly onChange: (patch: Partial<SettingsDraft>) => void;
}) {
  const { t } = useI18n();
  const on = draft.features.achievements;

  return (
    <fieldset style={fieldsetStyle}>
      <legend style={legendStyle}>{t('dashboard.loyaltyAdmin.achievements.heading')}</legend>
      <p style={metaStyle}>{t('dashboard.loyaltyAdmin.achievements.sub')}</p>

      <div style={{ marginTop: 'var(--space-3)' }}>
        <ToggleRow
          label={t('dashboard.loyaltyAdmin.achievements.enableLabel')}
          description={
            on
              ? t('dashboard.loyaltyAdmin.achievements.enableOn')
              : t('dashboard.loyaltyAdmin.achievements.enableOff')
          }
          checked={on}
          onChange={(v) => onChange({ features: { ...draft.features, achievements: v } })}
        />
      </div>

      {on ? (
        <div style={{ marginTop: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)' }}>
          <p style={{ ...metaStyle, margin: 0 }}>
            {t('dashboard.loyaltyAdmin.achievements.manageIntro')}
          </p>
          <Link
            to="/loyalty/achievements"
            style={{
              ...primaryButtonStyle,
              textDecoration: 'none',
              display: 'inline-block',
              justifySelf: 'start',
            }}
          >
            {t('dashboard.loyaltyAdmin.achievements.manageLink')}
          </Link>
        </div>
      ) : null}
    </fieldset>
  );
}

// ── streaks section (live toggle + fixed-definition explainer) ───────────
//
// Streaks need no extra config: the definition is fixed (consecutive Mon–Sun
// weeks with at least one paid wash, in the outlet's timezone), computed in the
// award tx by the pure `advanceStreak`. The operator only opts in/out, so a
// single toggle + a short explainer is the whole surface — no per-tenant knobs.
function StreaksSection({
  draft,
  onChange,
}: {
  readonly draft: SettingsDraft;
  readonly onChange: (patch: Partial<SettingsDraft>) => void;
}) {
  const { t } = useI18n();
  const on = draft.features.streaks;

  return (
    <fieldset style={fieldsetStyle}>
      <legend style={legendStyle}>{t('dashboard.loyaltyAdmin.streaks.heading')}</legend>
      <p style={metaStyle}>{t('dashboard.loyaltyAdmin.streaks.sub')}</p>

      <div style={{ marginTop: 'var(--space-3)' }}>
        <ToggleRow
          label={t('dashboard.loyaltyAdmin.streaks.enableLabel')}
          description={
            on
              ? t('dashboard.loyaltyAdmin.streaks.enableOn')
              : t('dashboard.loyaltyAdmin.streaks.enableOff')
          }
          checked={on}
          onChange={(v) => onChange({ features: { ...draft.features, streaks: v } })}
        />
      </div>

      {on ? (
        <p style={{ ...metaStyle, marginTop: 'var(--space-3)' }}>
          {t('dashboard.loyaltyAdmin.streaks.explainer')}
        </p>
      ) : null}
    </fieldset>
  );
}

// ── challenges section (live toggle + link to the authoring page) ────────
//
// Challenges are TIME-BOUNDED windowed goals, authored on the dedicated
// /loyalty/challenges CRUD page (mirrors how vouchers live on their own route).
// This section is just the master opt-in toggle the engine reads at award time
// plus a link into the authoring page — no per-tenant config lives here.
function ChallengesSection({
  draft,
  onChange,
}: {
  readonly draft: SettingsDraft;
  readonly onChange: (patch: Partial<SettingsDraft>) => void;
}) {
  const { t } = useI18n();
  const on = draft.features.challenges;

  return (
    <fieldset style={fieldsetStyle}>
      <legend style={legendStyle}>{t('dashboard.loyaltyAdmin.challenges.heading')}</legend>
      <p style={metaStyle}>{t('dashboard.loyaltyAdmin.challenges.sub')}</p>

      <div style={{ marginTop: 'var(--space-3)' }}>
        <ToggleRow
          label={t('dashboard.loyaltyAdmin.challenges.enableLabel')}
          description={
            on
              ? t('dashboard.loyaltyAdmin.challenges.enableOn')
              : t('dashboard.loyaltyAdmin.challenges.enableOff')
          }
          checked={on}
          onChange={(v) => onChange({ features: { ...draft.features, challenges: v } })}
        />
      </div>

      {on ? (
        <div style={{ marginTop: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)' }}>
          <p style={{ ...metaStyle, margin: 0 }}>
            {t('dashboard.loyaltyAdmin.challenges.manageIntro')}
          </p>
          <Link
            to="/loyalty/challenges"
            style={{
              ...primaryButtonStyle,
              textDecoration: 'none',
              display: 'inline-block',
              justifySelf: 'start',
            }}
          >
            {t('dashboard.loyaltyAdmin.challenges.manageLink')}
          </Link>
        </div>
      ) : null}
    </fieldset>
  );
}

// ── spin-wheel section (status + link to the authoring page) ─────────────
//
// The Spin Wheel is an operator-configured prize game. Unlike the other
// mechanics, its enable toggle is COUPLED to its prize table (a customer can't
// spin a wheel with < 2 segments), so BOTH the toggle and the segment authoring
// live on the dedicated /loyalty/spin-wheel page (mirrors how vouchers live on
// their own route). This section just surfaces the current on/off state + prize
// count and links into that page — no writable control here, so saving these
// settings can never enable a wheel with nothing to win.
function SpinWheelSection({ config }: { readonly config: LoyaltyConfig | null }) {
  const { t } = useI18n();
  const on = config?.enabledFeatures.spinWheel === true;
  const segmentCount = config?.spinWheel?.segments.length ?? 0;

  return (
    <fieldset style={fieldsetStyle}>
      <legend style={legendStyle}>{t('dashboard.loyaltyAdmin.spinWheel.heading')}</legend>
      <p style={metaStyle}>{t('dashboard.loyaltyAdmin.spinWheel.sub')}</p>

      <div style={{ marginTop: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)' }}>
        <p style={{ ...metaStyle, margin: 0 }}>
          {on
            ? t('dashboard.loyaltyAdmin.spinWheel.statusOn', { count: segmentCount })
            : segmentCount > 0
              ? t('dashboard.loyaltyAdmin.spinWheel.statusOffAuthored', { count: segmentCount })
              : t('dashboard.loyaltyAdmin.spinWheel.statusOffEmpty')}
        </p>
        <Link
          to="/loyalty/spin-wheel"
          style={{
            ...primaryButtonStyle,
            textDecoration: 'none',
            display: 'inline-block',
            justifySelf: 'start',
          }}
        >
          {t('dashboard.loyaltyAdmin.spinWheel.manageLink')}
        </Link>
      </div>
    </fieldset>
  );
}

// ── referrals section (live toggle + dual-reward pickers) ────────────────
//
// Customer-to-customer "refer a friend" (distinct from the B2B agent channel).
// The operator opts in and picks what each side earns when a referred NEW
// customer completes their first paid wash: the REFERRER's reward + the new
// friend's (REFEREE) reward. Each side is points / a voucher template / none,
// mirroring how punch + challenge rewards are configured. The engine grants
// both on that first paid wash (onTicketCollected → awardReferralReward).
function ReferralsSection({
  draft,
  templates,
  templatesLoading,
  onChange,
}: {
  readonly draft: SettingsDraft;
  readonly templates: readonly VoucherTemplate[];
  readonly templatesLoading: boolean;
  readonly onChange: (patch: Partial<SettingsDraft>) => void;
}) {
  const { t } = useI18n();
  const on = draft.features.referrals;

  return (
    <fieldset style={fieldsetStyle}>
      <legend style={legendStyle}>{t('dashboard.loyaltyAdmin.referrals.heading')}</legend>
      <p style={metaStyle}>{t('dashboard.loyaltyAdmin.referrals.sub')}</p>

      <div style={{ marginTop: 'var(--space-3)' }}>
        <ToggleRow
          label={t('dashboard.loyaltyAdmin.referrals.enableLabel')}
          description={
            on
              ? t('dashboard.loyaltyAdmin.referrals.enableOn')
              : t('dashboard.loyaltyAdmin.referrals.enableOff')
          }
          checked={on}
          onChange={(v) => onChange({ features: { ...draft.features, referrals: v } })}
        />
      </div>

      {on ? (
        <div style={{ display: 'grid', gap: 'var(--space-4)', marginTop: 'var(--space-4)' }}>
          <RewardPicker
            title={t('dashboard.loyaltyAdmin.referrals.refereeTitle')}
            hint={t('dashboard.loyaltyAdmin.referrals.refereeHint')}
            reward={draft.refereeReward}
            templates={templates}
            templatesLoading={templatesLoading}
            onChange={(refereeReward) => onChange({ refereeReward })}
          />
          <RewardPicker
            title={t('dashboard.loyaltyAdmin.referrals.referrerTitle')}
            hint={t('dashboard.loyaltyAdmin.referrals.referrerHint')}
            reward={draft.referrerReward}
            templates={templates}
            templatesLoading={templatesLoading}
            onChange={(referrerReward) => onChange({ referrerReward })}
          />
          <p style={hintStyle}>{t('dashboard.loyaltyAdmin.referrals.timing')}</p>
        </div>
      ) : null}
    </fieldset>
  );
}

// ── birthday-bonus section (live toggle + single reward picker) ──────────
//
// A once-a-year treat granted on a paid wash during the customer's opted-in
// birth MONTH (month-only — the customer never gives a day or year; owner
// privacy preference). Visit-triggered (no proactive/cron gifting), once per
// calendar year (enforced by the birthdayBonusYear marker on the account). The
// operator opts in and picks ONE reward (points / a voucher / none) — mirroring
// the referral reward pickers. The engine grants it in onTicketCollected →
// awardBirthdayBonus.
function BirthdayBonusSection({
  draft,
  templates,
  templatesLoading,
  onChange,
}: {
  readonly draft: SettingsDraft;
  readonly templates: readonly VoucherTemplate[];
  readonly templatesLoading: boolean;
  readonly onChange: (patch: Partial<SettingsDraft>) => void;
}) {
  const { t } = useI18n();
  const on = draft.features.birthdayBonus;

  return (
    <fieldset style={fieldsetStyle}>
      <legend style={legendStyle}>{t('dashboard.loyaltyAdmin.birthdayBonus.heading')}</legend>
      <p style={metaStyle}>{t('dashboard.loyaltyAdmin.birthdayBonus.sub')}</p>

      <div style={{ marginTop: 'var(--space-3)' }}>
        <ToggleRow
          label={t('dashboard.loyaltyAdmin.birthdayBonus.enableLabel')}
          description={
            on
              ? t('dashboard.loyaltyAdmin.birthdayBonus.enableOn')
              : t('dashboard.loyaltyAdmin.birthdayBonus.enableOff')
          }
          checked={on}
          onChange={(v) => onChange({ features: { ...draft.features, birthdayBonus: v } })}
        />
      </div>

      {on ? (
        <div style={{ display: 'grid', gap: 'var(--space-4)', marginTop: 'var(--space-4)' }}>
          <RewardPicker
            title={t('dashboard.loyaltyAdmin.birthdayBonus.rewardTitle')}
            hint={t('dashboard.loyaltyAdmin.birthdayBonus.rewardHint')}
            reward={draft.birthdayReward}
            templates={templates}
            templatesLoading={templatesLoading}
            onChange={(birthdayReward) => onChange({ birthdayReward })}
          />
          <p style={hintStyle}>{t('dashboard.loyaltyAdmin.birthdayBonus.timing')}</p>
        </div>
      ) : null}
    </fieldset>
  );
}

/** One reward-side editor: kind (none/points/voucher) + the matching input. */
function RewardPicker({
  title,
  hint,
  reward,
  templates,
  templatesLoading,
  onChange,
}: {
  readonly title: string;
  readonly hint: string;
  readonly reward: RewardDraft;
  readonly templates: readonly VoucherTemplate[];
  readonly templatesLoading: boolean;
  readonly onChange: (r: RewardDraft) => void;
}) {
  const { t, locale } = useI18n();
  const noTemplates = !templatesLoading && templates.length === 0;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <div>
        <span style={{ fontWeight: 500, fontSize: 'var(--text-body)' }}>{title}</span>
        <p style={metaStyle}>{hint}</p>
      </div>
      <label style={fieldStyle}>
        <span style={fieldLabelStyle}>{t('dashboard.loyaltyAdmin.referrals.rewardKind')}</span>
        <select
          value={reward.kind}
          onChange={(e) => onChange({ ...reward, kind: e.target.value as RewardDraft['kind'] })}
          style={inputStyle}
        >
          <option value="none">{t('dashboard.loyaltyAdmin.referrals.kind.none')}</option>
          <option value="points">{t('dashboard.loyaltyAdmin.referrals.kind.points')}</option>
          <option value="voucher">{t('dashboard.loyaltyAdmin.referrals.kind.voucher')}</option>
        </select>
      </label>

      {reward.kind === 'points' ? (
        <NumberField
          label={t('dashboard.loyaltyAdmin.referrals.pointsLabel')}
          hint={t('dashboard.loyaltyAdmin.referrals.pointsHint')}
          value={reward.amount}
          onChange={(v) => onChange({ ...reward, amount: v })}
        />
      ) : null}

      {reward.kind === 'voucher' ? (
        noTemplates ? (
          <div style={emptyStyle}>
            <p style={{ ...metaStyle, margin: 0 }}>
              {t('dashboard.loyaltyAdmin.referrals.noTemplates')}
            </p>
            <Link
              to="/loyalty/vouchers"
              style={{
                ...primaryButtonStyle,
                marginTop: 'var(--space-3)',
                textDecoration: 'none',
                display: 'inline-block',
              }}
            >
              {t('dashboard.loyaltyAdmin.referrals.createVoucher')}
            </Link>
          </div>
        ) : (
          <label style={fieldStyle}>
            <span style={fieldLabelStyle}>
              {t('dashboard.loyaltyAdmin.referrals.voucherLabel')}
            </span>
            <select
              value={reward.voucherTemplateId}
              onChange={(e) => onChange({ ...reward, voucherTemplateId: e.target.value })}
              style={inputStyle}
            >
              <option value="">{t('dashboard.loyaltyAdmin.referrals.voucherNone')}</option>
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name[locale] ?? tpl.name.en ?? tpl.id}
                </option>
              ))}
            </select>
          </label>
        )
      ) : null}
    </div>
  );
}

function StatusText({ stage, isDirty }: { readonly stage: SaveStage; readonly isDirty: boolean }) {
  const { t } = useI18n();
  if (stage.kind === 'saved' && !isDirty) {
    return (
      <span style={{ ...statusStyle, color: 'var(--color-primary)' }}>
        {t('dashboard.loyaltyAdmin.settings.saved')}
      </span>
    );
  }
  if (stage.kind === 'failed') {
    return (
      <span role="alert" style={{ ...statusStyle, color: 'var(--color-accent)' }}>
        {stage.message}
      </span>
    );
  }
  if (!isDirty) {
    return <span style={statusStyle}>{t('dashboard.loyaltyAdmin.settings.noChanges')}</span>;
  }
  return null;
}

// ══════════════════════════════════════════════════════════════════════════
// 2. Tier-ladder editor
// ══════════════════════════════════════════════════════════════════════════

function TierLadderEditor({ tenantId }: { readonly tenantId: string }) {
  const { t } = useI18n();
  const repo = useMemo(() => loyaltyTiersRepoFor(tenantId), [tenantId]);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const subscribe = useMemo(
    () => (cb: (v: Tier[]) => void) => repo.listSnapshot(cb, (e) => setLoadError(e)),
    [repo],
  );
  const { data: tiers, loading } = useTrackedSnapshot<Tier[]>(
    ['loyalty-tiers', tenantId],
    subscribe,
  );

  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const sorted = useMemo(() => (tiers ? [...tiers].sort((a, b) => a.rank - b.rank) : []), [tiers]);
  const nextRank = sorted.length > 0 ? Math.max(...sorted.map((x) => x.rank)) + 1 : 0;
  const lastTier = sorted.length > 0 ? sorted[sorted.length - 1] : undefined;
  const nextThreshold = lastTier ? lastTier.thresholdLifetimePoints + 500 : 0;

  return (
    <Surface as="article">
      <div style={sectionHeadRowStyle}>
        <SectionHead
          title={t('dashboard.loyaltyAdmin.tiers.heading')}
          sub={t('dashboard.loyaltyAdmin.tiers.sub')}
        />
        {tiers && !creating ? (
          <button type="button" onClick={() => setCreating(true)} style={primaryButtonStyle}>
            {t('dashboard.loyaltyAdmin.tiers.addTier')}
          </button>
        ) : null}
      </div>

      <div style={{ display: 'grid', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
        {creating ? (
          <TierForm
            tenantId={tenantId}
            mode="create"
            defaults={{ rank: nextRank, threshold: nextThreshold }}
            onDone={() => setCreating(false)}
          />
        ) : null}

        {loadError && !tiers ? (
          <FeedbackCard
            title={t('dashboard.loyaltyAdmin.tiers.errorTitle')}
            body={t('dashboard.loyaltyAdmin.tiers.errorBody')}
            actionLabel={t('dashboard.loyaltyAdmin.settings.reload')}
            onAction={() => window.location.reload()}
          />
        ) : loading && !tiers ? (
          <TierSkeleton />
        ) : sorted.length === 0 && !creating ? (
          <EmptyTiers onAdd={() => setCreating(true)} />
        ) : (
          sorted.map((tier) =>
            editingId === tier.id ? (
              <TierForm
                key={tier.id}
                tenantId={tenantId}
                mode="edit"
                tier={tier}
                onDone={() => setEditingId(null)}
              />
            ) : (
              <TierRow
                key={tier.id}
                tenantId={tenantId}
                tier={tier}
                onEdit={() => setEditingId(tier.id)}
              />
            ),
          )
        )}
      </div>
    </Surface>
  );
}

function TierRow({
  tenantId,
  tier,
  onEdit,
}: {
  readonly tenantId: string;
  readonly tier: Tier;
  readonly onEdit: () => void;
}) {
  const { t, locale } = useI18n();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function doDelete() {
    setDeleting(true);
    setErr(null);
    try {
      await loyaltyTiersRepoFor(tenantId).delete(tier.id);
      logger.info('loyalty tier deleted', { tenantId, tierId: tier.id });
      // Row disappears when the snapshot updates; no local state to reset.
    } catch (e) {
      setErr(t('dashboard.loyaltyAdmin.tiers.deleteFailed', { error: (e as Error).message ?? '' }));
      setDeleting(false);
      setConfirming(false);
    }
  }

  const name = tier.name[locale] ?? tier.name.en;

  return (
    <div style={tierRowStyle}>
      <span aria-hidden style={{ ...badgeDotStyle, background: tier.badge.colour }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={tierNameStyle}>
          {name}{' '}
          <span style={rankChipStyle}>
            {t('dashboard.loyaltyAdmin.tiers.rankLabel', { n: tier.rank })}
          </span>
        </p>
        <p style={metaStyle}>
          {t('dashboard.loyaltyAdmin.tiers.threshold', { n: tier.thresholdLifetimePoints })}
        </p>
        {tier.perks.length > 0 ? (
          <div style={perkChipsStyle}>
            {tier.perks.map((p, i) => (
              <span key={i} style={perkChipStyle}>
                {p.description[locale] ?? p.description.en}
              </span>
            ))}
          </div>
        ) : (
          <p style={metaStyle}>{t('dashboard.loyaltyAdmin.tiers.noPerks')}</p>
        )}
        {err ? <p style={{ ...statusStyle, color: 'var(--color-accent)' }}>{err}</p> : null}
      </div>
      <div style={tierActionsStyle}>
        {confirming ? (
          <>
            <button
              type="button"
              disabled={deleting}
              onClick={() => void doDelete()}
              style={dangerButtonStyle}
            >
              {deleting
                ? t('dashboard.loyaltyAdmin.tiers.deleting')
                : t('dashboard.loyaltyAdmin.tiers.confirmDeleteYes')}
            </button>
            <button type="button" onClick={() => setConfirming(false)} style={ghostButtonStyle}>
              {t('dashboard.loyaltyAdmin.tiers.cancel')}
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={onEdit} style={ghostButtonStyle}>
              {t('dashboard.loyaltyAdmin.tiers.edit')}
            </button>
            <button type="button" onClick={() => setConfirming(true)} style={ghostButtonStyle}>
              {t('dashboard.loyaltyAdmin.tiers.delete')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── tier form (create + edit) ────────────────────────────────────────────

interface PerkDraft {
  key: string;
  kind: TierPerk['kind'];
  value: string;
  period: 'monthly' | 'quarterly';
  descriptionEn: string;
  originalDescription: I18nString | null;
}

interface TierDraftState {
  nameEn: string;
  originalName: I18nString | null;
  threshold: string;
  rank: string;
  badgeColour: string;
  badgeIcon: string;
  perks: PerkDraft[];
}

let perkKeySeq = 0;
function newPerkKey(): string {
  perkKeySeq += 1;
  return `perk-${perkKeySeq}`;
}

function perkToDraft(p: TierPerk): PerkDraft {
  return {
    key: newPerkKey(),
    kind: p.kind,
    value: p.kind === 'discount_pct' || p.kind === 'bonus_points_multiplier' ? String(p.value) : '',
    period: p.kind === 'free_upgrade_per_period' ? p.period : 'monthly',
    descriptionEn: p.description.en,
    originalDescription: p.description,
  };
}

function tierToDraft(tier: Tier): TierDraftState {
  return {
    nameEn: tier.name.en,
    originalName: tier.name,
    threshold: String(tier.thresholdLifetimePoints),
    rank: String(tier.rank),
    badgeColour: tier.badge.colour,
    badgeIcon: tier.badge.icon,
    perks: tier.perks.map(perkToDraft),
  };
}

function TierForm({
  tenantId,
  mode,
  tier,
  defaults,
  onDone,
}: {
  readonly tenantId: string;
  readonly mode: 'create' | 'edit';
  readonly tier?: Tier;
  readonly defaults?: { rank: number; threshold: number };
  readonly onDone: () => void;
}) {
  const { t } = useI18n();
  const [state, setState] = useState<TierDraftState>(() =>
    tier
      ? tierToDraft(tier)
      : {
          nameEn: '',
          originalName: null,
          threshold: String(defaults?.threshold ?? 0),
          rank: String(defaults?.rank ?? 0),
          badgeColour: '#a8a8a8',
          badgeIcon: 'medal',
          perks: [],
        },
  );
  const [stage, setStage] = useState<SaveStage>({ kind: 'idle' });

  function patch(p: Partial<TierDraftState>) {
    setState((s) => ({ ...s, ...p }));
    setStage({ kind: 'idle' });
  }

  function addPerk() {
    patch({
      perks: [
        ...state.perks,
        {
          key: newPerkKey(),
          kind: 'discount_pct',
          value: '5',
          period: 'monthly',
          descriptionEn: '',
          originalDescription: null,
        },
      ],
    });
  }

  function updatePerk(key: string, p: Partial<PerkDraft>) {
    patch({ perks: state.perks.map((x) => (x.key === key ? { ...x, ...p } : x)) });
  }

  function removePerk(key: string) {
    patch({ perks: state.perks.filter((x) => x.key !== key) });
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (state.nameEn.trim().length === 0) {
      setStage({ kind: 'failed', message: t('dashboard.loyaltyAdmin.tierForm.nameRequired') });
      return;
    }
    const threshold = Number.parseInt(state.threshold, 10);
    if (!Number.isFinite(threshold) || threshold < 0) {
      setStage({ kind: 'failed', message: t('dashboard.loyaltyAdmin.tierForm.invalidThreshold') });
      return;
    }
    for (const p of state.perks) {
      if (p.descriptionEn.trim().length === 0) {
        setStage({
          kind: 'failed',
          message: t('dashboard.loyaltyAdmin.tierForm.perkDescRequired'),
        });
        return;
      }
    }
    setStage({ kind: 'saving' });
    try {
      const rank = Number.parseInt(state.rank, 10);
      const id = tier?.id ?? `tier-${Date.now().toString(36)}`;
      const next: Tier = {
        id,
        rank: Number.isFinite(rank) ? rank : 0,
        name: { ...(state.originalName ?? {}), en: state.nameEn.trim() },
        thresholdLifetimePoints: threshold,
        perks: state.perks.map(buildPerk),
        badge: {
          icon: state.badgeIcon.trim() || 'medal',
          colour: state.badgeColour.trim() || '#a8a8a8',
        },
      };
      await loyaltyTiersRepoFor(tenantId).set(id, next);
      logger.info('loyalty tier saved', { tenantId, tierId: id, mode });
      onDone();
    } catch (err) {
      setStage({
        kind: 'failed',
        message: t('dashboard.loyaltyAdmin.tierForm.saveFailed', {
          error: (err as Error).message ?? '',
        }),
      });
    }
  }

  return (
    <form onSubmit={submit} style={tierFormStyle}>
      <h3 style={tierFormTitleStyle}>
        {mode === 'create'
          ? t('dashboard.loyaltyAdmin.tierForm.newTitle')
          : t('dashboard.loyaltyAdmin.tierForm.editTitle')}
      </h3>

      <TextField
        label={t('dashboard.loyaltyAdmin.tierForm.name')}
        hint={t('dashboard.loyaltyAdmin.tierForm.nameHint')}
        placeholder={t('dashboard.loyaltyAdmin.tierForm.namePlaceholder')}
        value={state.nameEn}
        onChange={(v) => patch({ nameEn: v })}
      />

      <div style={twoColStyle}>
        <NumberField
          label={t('dashboard.loyaltyAdmin.tierForm.threshold')}
          value={state.threshold}
          onChange={(v) => patch({ threshold: v })}
        />
        <NumberField
          label={t('dashboard.loyaltyAdmin.tierForm.rank')}
          hint={t('dashboard.loyaltyAdmin.tierForm.rankHint')}
          value={state.rank}
          onChange={(v) => patch({ rank: v })}
        />
      </div>

      <div style={twoColStyle}>
        <label style={fieldStyle}>
          <span style={fieldLabelStyle}>{t('dashboard.loyaltyAdmin.tierForm.badgeColour')}</span>
          <input
            type="color"
            value={/^#[0-9a-fA-F]{6}$/.test(state.badgeColour) ? state.badgeColour : '#a8a8a8'}
            onChange={(e) => patch({ badgeColour: e.target.value })}
            style={{ ...inputStyle, height: 44, padding: 4 }}
          />
        </label>
        <TextField
          label={t('dashboard.loyaltyAdmin.tierForm.badgeIcon')}
          hint={t('dashboard.loyaltyAdmin.tierForm.badgeIconHint')}
          value={state.badgeIcon}
          onChange={(v) => patch({ badgeIcon: v })}
        />
      </div>

      <fieldset style={fieldsetStyle}>
        <legend style={legendStyle}>{t('dashboard.loyaltyAdmin.tierForm.perksHeading')}</legend>
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          {state.perks.map((p) => (
            <PerkRow
              key={p.key}
              perk={p}
              onChange={(patchP) => updatePerk(p.key, patchP)}
              onRemove={() => removePerk(p.key)}
            />
          ))}
          <button
            type="button"
            onClick={addPerk}
            style={{ ...ghostButtonStyle, justifySelf: 'start' }}
          >
            {t('dashboard.loyaltyAdmin.tierForm.addPerk')}
          </button>
        </div>
      </fieldset>

      <div style={saveBarStyle}>
        <button type="submit" disabled={stage.kind === 'saving'} style={primaryButtonStyle}>
          {stage.kind === 'saving'
            ? t('dashboard.loyaltyAdmin.tierForm.saving')
            : mode === 'create'
              ? t('dashboard.loyaltyAdmin.tierForm.create')
              : t('dashboard.loyaltyAdmin.tierForm.save')}
        </button>
        <button type="button" onClick={onDone} style={ghostButtonStyle}>
          {t('dashboard.loyaltyAdmin.tierForm.cancel')}
        </button>
        {stage.kind === 'failed' ? (
          <span role="alert" style={{ ...statusStyle, color: 'var(--color-accent)' }}>
            {stage.message}
          </span>
        ) : null}
      </div>
    </form>
  );
}

function buildPerk(p: PerkDraft): TierPerk {
  const description: I18nString = { ...(p.originalDescription ?? {}), en: p.descriptionEn.trim() };
  switch (p.kind) {
    case 'discount_pct': {
      const v = Number(p.value);
      return {
        kind: 'discount_pct',
        value: Number.isFinite(v) ? Math.min(100, Math.max(0, v)) : 0,
        description,
      };
    }
    case 'bonus_points_multiplier': {
      const v = Number(p.value);
      return {
        kind: 'bonus_points_multiplier',
        value: Number.isFinite(v) && v > 0 ? v : 1,
        description,
      };
    }
    case 'free_upgrade_per_period':
      return { kind: 'free_upgrade_per_period', period: p.period, description };
    case 'queue_priority':
      return { kind: 'queue_priority', description };
    case 'custom':
      return { kind: 'custom', description };
  }
}

function PerkRow({
  perk,
  onChange,
  onRemove,
}: {
  readonly perk: PerkDraft;
  readonly onChange: (p: Partial<PerkDraft>) => void;
  readonly onRemove: () => void;
}) {
  const { t } = useI18n();
  const showValue = perk.kind === 'discount_pct' || perk.kind === 'bonus_points_multiplier';
  const showPeriod = perk.kind === 'free_upgrade_per_period';
  return (
    <div style={perkRowStyle}>
      <div style={perkTopStyle}>
        <label style={{ ...fieldStyle, flex: 1 }}>
          <span style={fieldLabelStyle}>{t('dashboard.loyaltyAdmin.tierForm.perkKind')}</span>
          <select
            value={perk.kind}
            onChange={(e) => onChange({ kind: e.target.value as TierPerk['kind'] })}
            style={inputStyle}
          >
            {PERK_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`dashboard.loyaltyAdmin.tierForm.kind.${k}`)}
              </option>
            ))}
          </select>
        </label>
        {showValue ? (
          <label style={{ ...fieldStyle, width: 140 }}>
            <span style={fieldLabelStyle}>
              {perk.kind === 'discount_pct'
                ? t('dashboard.loyaltyAdmin.tierForm.perkPercent')
                : t('dashboard.loyaltyAdmin.tierForm.perkMultiplier')}
            </span>
            <input
              inputMode="decimal"
              value={perk.value}
              onChange={(e) => onChange({ value: e.target.value.replace(/[^\d.]/g, '') })}
              style={inputStyle}
            />
          </label>
        ) : null}
        {showPeriod ? (
          <label style={{ ...fieldStyle, width: 160 }}>
            <span style={fieldLabelStyle}>{t('dashboard.loyaltyAdmin.tierForm.perkPeriod')}</span>
            <select
              value={perk.period}
              onChange={(e) => onChange({ period: e.target.value as 'monthly' | 'quarterly' })}
              style={inputStyle}
            >
              <option value="monthly">{t('dashboard.loyaltyAdmin.tierForm.period.monthly')}</option>
              <option value="quarterly">
                {t('dashboard.loyaltyAdmin.tierForm.period.quarterly')}
              </option>
            </select>
          </label>
        ) : null}
      </div>
      <label style={fieldStyle}>
        <span style={fieldLabelStyle}>{t('dashboard.loyaltyAdmin.tierForm.perkDescription')}</span>
        <input
          value={perk.descriptionEn}
          onChange={(e) => onChange({ descriptionEn: e.target.value })}
          style={inputStyle}
        />
      </label>
      <Button variant="danger" onClick={onRemove} style={{ justifySelf: 'start' }}>
        {t('dashboard.loyaltyAdmin.tierForm.removePerk')}
      </Button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// shared primitives
// ══════════════════════════════════════════════════════════════════════════

function SectionHead({ title, sub }: { readonly title: string; readonly sub: string }) {
  return (
    <div>
      <h2 style={h2Style}>{title}</h2>
      <p style={metaStyle}>{sub}</p>
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  compact,
}: {
  readonly label: string;
  readonly description?: string;
  readonly checked: boolean;
  readonly onChange: (v: boolean) => void;
  readonly compact?: boolean;
}) {
  return (
    <label style={compact ? toggleRowCompactStyle : toggleRowStyle}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 500, fontSize: 'var(--text-body)' }}>{label}</span>
        {description ? <p style={metaStyle}>{description}</p> : null}
      </div>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: 'var(--color-primary)', width: 20, height: 20, flexShrink: 0 }}
      />
    </label>
  );
}

function NumberField({
  label,
  hint,
  value,
  onChange,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
}) {
  return (
    <label style={fieldStyle}>
      <span style={fieldLabelStyle}>{label}</span>
      <input
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))}
        style={inputStyle}
      />
      {hint ? <span style={hintStyle}>{hint}</span> : null}
    </label>
  );
}

function TextField({
  label,
  hint,
  placeholder,
  value,
  onChange,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly placeholder?: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
}) {
  return (
    <label style={fieldStyle}>
      <span style={fieldLabelStyle}>{label}</span>
      <input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
      />
      {hint ? <span style={hintStyle}>{hint}</span> : null}
    </label>
  );
}

function FeedbackCard({
  title,
  body,
  actionLabel,
  onAction,
}: {
  readonly title: string;
  readonly body: string;
  readonly actionLabel: string;
  readonly onAction: () => void;
}) {
  return (
    <div style={errorCardStyle} role="alert">
      <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-accent)' }}>{title}</p>
      <p style={{ ...metaStyle, marginTop: 'var(--space-1)' }}>{body}</p>
      <button
        type="button"
        onClick={onAction}
        style={{ ...ghostButtonStyle, marginTop: 'var(--space-3)', justifySelf: 'start' }}
      >
        {actionLabel}
      </button>
    </div>
  );
}

function EmptyTiers({ onAdd }: { readonly onAdd: () => void }) {
  const { t } = useI18n();
  return (
    <div style={emptyStyle}>
      <p style={{ ...metaStyle, margin: 0 }}>{t('dashboard.loyaltyAdmin.tiers.empty')}</p>
      <button
        type="button"
        onClick={onAdd}
        style={{ ...primaryButtonStyle, marginTop: 'var(--space-3)' }}
      >
        {t('dashboard.loyaltyAdmin.tiers.addTier')}
      </button>
    </div>
  );
}

function TierSkeleton() {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 72,
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-border)',
          }}
        />
      ))}
    </div>
  );
}

function InlineNote({ children }: { readonly children: React.ReactNode }) {
  return <p style={{ ...metaStyle, marginTop: 'var(--space-4)' }}>{children}</p>;
}

function Splash({ label }: { readonly label: string }) {
  return (
    <main style={pageStyle}>
      <section
        style={{ ...cardStyle, maxWidth: 480, margin: 'var(--space-8) auto', textAlign: 'center' }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {label}
        </p>
      </section>
    </main>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// styles
// ══════════════════════════════════════════════════════════════════════════

const pageStyle: React.CSSProperties = { minHeight: '100dvh', background: 'var(--tint-subtle)' };

const headerStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderBottom: '1px solid var(--color-border)',
  marginBottom: 'var(--space-6)',
};

const headerInnerStyle: React.CSSProperties = {
  maxWidth: PAGE_FRAME_MAX,
  margin: '0 auto',
  padding: 'var(--space-5)',
  display: 'flex',
  gap: 'var(--space-4)',
  alignItems: 'center',
};

const bodyStyle: React.CSSProperties = {
  maxWidth: PAGE_FRAME_MAX,
  margin: '0 auto',
  padding: '0 var(--space-5) var(--space-12) var(--space-5)',
  display: 'grid',
  gap: 'var(--space-4)',
};

const sectionHeadRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-3)',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
};

const twoColStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
  gap: 'var(--space-3)',
};

const fieldsetStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-4)',
  margin: 0,
};

const legendStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  padding: '0 var(--space-2)',
};

const featureGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
  gap: 'var(--space-2)',
  marginTop: 'var(--space-3)',
};

const fieldStyle: React.CSSProperties = { display: 'grid', gap: 'var(--space-1)' };

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const hintStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-on-surface-muted)',
};

const toggleRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-3)',
  alignItems: 'center',
  padding: 'var(--space-3) var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border)',
  cursor: 'pointer',
};

const toggleRowCompactStyle: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
  alignItems: 'center',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border)',
  cursor: 'pointer',
  minHeight: 'var(--touch-target)',
};

/** A grid child that spans every column of `sectionGridStyle`. */

const saveBarStyle: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-3)',
  alignItems: 'center',
  flexWrap: 'wrap',
};

const statusStyle: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const tierRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-3)',
  alignItems: 'flex-start',
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-border)',
};

const badgeDotStyle: React.CSSProperties = {
  width: 14,
  height: 14,
  borderRadius: '50%',
  marginTop: 4,
  flexShrink: 0,
  boxShadow: 'inset 0 0 0 1px var(--color-border-strong)',
};

const tierNameStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-body)',
  fontWeight: 600,
  display: 'flex',
  gap: 'var(--space-2)',
  alignItems: 'center',
  flexWrap: 'wrap',
};

const rankChipStyle: React.CSSProperties = {
  fontSize: '0.6875rem',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-border)',
  color: 'var(--color-on-surface-muted)',
};

const perkChipsStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-1)',
  marginTop: 'var(--space-2)',
};

const perkChipStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  padding: '3px 10px',
  borderRadius: 'var(--radius-pill)',
  background: 'rgba(13,122,79,0.08)',
  color: 'var(--color-primary)',
};

const tierActionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
  flexShrink: 0,
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
};

const tierFormStyle: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--space-4)',
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid rgba(13,122,79,0.22)',
  background: 'rgba(13,122,79,0.03)',
};

const tierFormTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-body)',
  fontWeight: 600,
};

const perkRowStyle: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--space-2)',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface)',
};

const perkTopStyle: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--space-2)',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
};

const emptyStyle: React.CSSProperties = {
  padding: 'var(--space-6)',
  borderRadius: 'var(--radius-md)',
  border: '1px dashed var(--color-border-strong)',
  textAlign: 'center',
};

const errorCardStyle: React.CSSProperties = {
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-md)',
  background: 'rgba(239,68,68,0.05)',
  border: '1px solid rgba(239,68,68,0.20)',
  display: 'grid',
};

const h1Style: React.CSSProperties = {
  margin: 'var(--space-1) 0 0 0',
  fontSize: 'var(--text-h1)',
  fontWeight: 600,
  lineHeight: 1.2,
};

const h2Style: React.CSSProperties = { margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 };

const metaStyle: React.CSSProperties = {
  margin: 'var(--space-1) 0 0 0',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.5,
};
